<template>
  <b-modal
    id="modal-bulk-upload-zone-rate"
    ref="modal"
    title="Bulk Upload — Special Zone Delivery Rates"
    size="xl"
    no-close-on-backdrop
    @show="resetModal"
    @hidden="resetModal"
    ok-variant="primary primary-btn"
    ok-title="Upload"
    cancel-title="Close"
    :ok-disabled="!hasValidFile"
    @ok="handleOk"
  >
    <p class="text-muted mb-2">
      Download the template, fill in your records, then upload the CSV file.
    </p>
    <b-row class="mb-3 align-items-center">
      <b-col cols="auto">
        <strong>Template:</strong>
      </b-col>
      <b-col cols="auto">
        <b-button size="sm" variant="outline-secondary" @click="downloadTemplate">
          <download-icon size="1x" class="mr-1"></download-icon>
          Download CSV Template
        </b-button>
      </b-col>
    </b-row>

    <b-form-group label="Upload CSV File" label-for="zone-rate-file">
      <b-form-file
        id="zone-rate-file"
        v-model="zoneRateFile"
        accept=".csv"
        placeholder="Choose a .csv file..."
        @change="onFileChange"
      ></b-form-file>
      <small class="text-muted mt-1 d-block">
        Required columns: zone_name, rate_name, rate, cutoff_time (HH:mm), cutoff_date (YYYY-MM-DD), delivery_date (YYYY-MM-DD)
      </small>
    </b-form-group>

    <!-- Validation errors -->
    <b-alert v-if="errors.length > 0" variant="danger" show class="mt-2">
      <strong>Validation errors — please fix your CSV and re-upload:</strong>
      <ul class="mb-0 mt-1">
        <li v-for="(err, i) in errors" :key="i">{{ err }}</li>
      </ul>
    </b-alert>

    <!-- Preview table -->
    <div v-if="preview.length > 0" class="mt-3">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <strong>Preview ({{ preview.length }} row{{ preview.length !== 1 ? 's' : '' }})</strong>
        <b-badge variant="success">Ready to upload</b-badge>
      </div>
      <b-table
        :items="preview.slice(0, 10)"
        :fields="previewFields"
        small
        bordered
        striped
        responsive
        class="mt-1"
      >
        <template #cell(status_badge)="data">
          <b-badge :variant="data.item._valid ? 'success' : 'danger'">
            {{ data.item._valid ? 'OK' : 'Error' }}
          </b-badge>
        </template>
      </b-table>
      <p v-if="preview.length > 10" class="text-muted small">
        Showing first 10 of {{ preview.length }} rows.
      </p>
    </div>

    <!-- Upload progress / results -->
    <b-overlay :show="uploading" rounded="sm" class="mt-2">
      <template #overlay>
        <div class="text-center">
          <b-spinner type="grow" variant="info" label="Uploading..."></b-spinner>
          <p class="mt-2">Uploading {{ uploadProgress.current }} of {{ uploadProgress.total }}...</p>
        </div>
      </template>
      <div v-if="uploadResults.length > 0" class="mt-3">
        <strong>Upload Results:</strong>
        <b-table
          :items="uploadResults"
          :fields="uploadResultFields"
          small
          bordered
          striped
          responsive
          class="mt-1"
        >
          <template #cell(result)="data">
            <b-badge :variant="data.item.success ? 'success' : 'danger'">
              {{ data.item.success ? 'Success' : 'Failed' }}
            </b-badge>
          </template>
        </b-table>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { DownloadIcon } from "vue-feather-icons";

const REQUIRED_COLUMNS = [
  "zone_name",
  "rate_name",
  "rate",
  "cutoff_time",
  "cutoff_date",
  "delivery_date"
];

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_REGEX = /^\d{1,2}:\d{2}(:\d{2})?$/;

export default {
  name: "bulk-upload-popup",
  components: { DownloadIcon },
  props: ["zoneOptions"],
  data() {
    return {
      uploading: false,
      uploadProgress: { current: 0, total: 0 },
      uploadResults: [],
      uploadResultFields: [
        { key: "row", label: "Row" },
        { key: "name", label: "Name" },
        { key: "result", label: "Result" },
        { key: "message", label: "Message" }
      ],
      zoneRateFile: null,
      preview: [],
      errors: [],
      previewFields: [
        { key: "zone_name", label: "Zone Name" },
        { key: "rate_name", label: "Rate Name" },
        { key: "rate", label: "Rate" },
        { key: "cutoff_time", label: "Cutoff Time" },
        { key: "cutoff_date", label: "Cutoff Date" },
        { key: "delivery_date", label: "Delivery Date" },
        { key: "status_badge", label: "Valid" }
      ]
    };
  },
  computed: {
    hasValidFile() {
      return this.preview.length > 0 && this.errors.length === 0;
    }
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    resetModal() {
      this.uploading = false;
      this.uploadProgress = { current: 0, total: 0 };
      this.uploadResults = [];
      this.zoneRateFile = null;
      this.preview = [];
      this.errors = [];
    },

    // ── Template download ─────────────────────────────────────────────────────

    downloadTemplate() {
      const headers = REQUIRED_COLUMNS.join(",");
      const example =
        "Winnipeg,Anytime (8am - 8pm) - $10,$10,14:00,2026-12-25,2026-12-26";
      this._triggerCsvDownload(
        `${headers}\n${example}`,
        "special_zone_delivery_rate_template.csv"
      );
    },

    _triggerCsvDownload(csvContent, filename) {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // ── File change ───────────────────────────────────────────────────────────

    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.errors = [];
      this.preview = [];
      this.uploadResults = [];
      this._readCsvFile(file, (rows) => {
        const { errors, data } = this._validateRows(rows);
        this.errors = errors;
        this.preview = data;
      });
    },

    // ── CSV parsing ───────────────────────────────────────────────────────────

    _readCsvFile(file, callback) {
      const reader = new FileReader();
      reader.onload = (e) => {
        callback(this._parseCsv(e.target.result));
      };
      reader.readAsText(file);
    },

    _parseCsv(text) {
      const lines = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter((l) => l.trim() !== "");

      if (lines.length < 2) return [];

      const headers = this._splitCsvLine(lines[0]).map((h) =>
        h.trim().toLowerCase()
      );
      return lines.slice(1).map((line, idx) => {
        const values = this._splitCsvLine(line);
        const row = {};
        headers.forEach((h, i) => {
          row[h] = (values[i] || "").trim();
        });
        row._rowNumber = idx + 2; // 1-indexed, header is row 1
        return row;
      });
    },

    _splitCsvLine(line) {
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += ch;
        }
      }
      result.push(current);
      return result;
    },

    // ── Validation ────────────────────────────────────────────────────────────

    _validateRows(rows) {
      const errors = [];
      const data = [];

      if (rows.length === 0) {
        errors.push("The file is empty or contains only a header row.");
        return { errors, data };
      }

      const missingCols = REQUIRED_COLUMNS.filter(
        (col) => !(col in rows[0])
      );
      if (missingCols.length > 0) {
        errors.push(
          `Missing required column(s): ${missingCols.join(", ")}. ` +
            `Expected: ${REQUIRED_COLUMNS.join(", ")}`
        );
        return { errors, data };
      }

      rows.forEach((row) => {
        const rowErrors = [];

        if (!row.zone_name) rowErrors.push("zone_name is required");
        if (!row.rate_name) rowErrors.push("rate_name is required");
        if (!row.rate) rowErrors.push("rate is required");

        if (!row.cutoff_time) {
          rowErrors.push("cutoff_time is required");
        } else if (!TIME_REGEX.test(row.cutoff_time)) {
          rowErrors.push(
            `cutoff_time "${row.cutoff_time}" must be H:mm or H:mm:ss`
          );
        }

        if (!row.cutoff_date) {
          rowErrors.push("cutoff_date is required");
        } else if (!DATE_REGEX.test(row.cutoff_date)) {
          rowErrors.push(
            `cutoff_date "${row.cutoff_date}" must be YYYY-MM-DD`
          );
        }

        if (!row.delivery_date) {
          rowErrors.push("delivery_date is required");
        } else if (!DATE_REGEX.test(row.delivery_date)) {
          rowErrors.push(
            `delivery_date "${row.delivery_date}" must be YYYY-MM-DD`
          );
        }

        if (rowErrors.length > 0) {
          rowErrors.forEach((e) =>
            errors.push(`Row ${row._rowNumber}: ${e}`)
          );
          data.push({ ...row, _valid: false });
        } else {
          data.push({ ...row, _valid: true });
        }
      });

      return { errors, data };
    },

    // ── Submit ────────────────────────────────────────────────────────────────

    async handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      await this.handleSubmit();
    },

    async handleSubmit() {
      this.uploadResults = [];
      const rows = this.preview.filter((r) => r._valid);
      if (rows.length === 0) return;

      this.uploading = true;
      this.uploadProgress = { current: 0, total: rows.length };

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        this.uploadProgress.current = i + 1;

        const zoneId = this._resolveZoneId(row.zone_name);
        if (!zoneId) {
          this.uploadResults.push({
            row: row._rowNumber,
            name: row.zone_name,
            success: false,
            message: `Zone "${row.zone_name}" not found. Check that zone_name matches exactly.`
          });
          failCount++;
          continue;
        }

        const cutoffTime =
          row.cutoff_time.length === 5
            ? `${row.cutoff_time}:00`
            : row.cutoff_time;
        const rateName = row.rate_name.split(" - $")[0];

        const payload = {
          name: `${row.zone_name} - ${rateName} - ${row.rate}`,
          zone_id: zoneId,
          rate_name: `${rateName} - ${row.rate}`,
          rate: row.rate,
          cutoff_time: cutoffTime.substring(0, 5),
          cutoff_date: row.cutoff_date,
          delivery_date: row.delivery_date
        };
        console.log("payload:", JSON.stringify(payload));

        try {
          const response = await this.axios({
            method: "POST",
            url: `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/rates/special/zone/rate`,
            headers: {
              Authorization: `Bearer ${this.$store.getters.idToken}`
            },
            data: payload
          });

          if (response.status === 201 || response.status === 200) {
            this.uploadResults.push({
              row: row._rowNumber,
              name: payload.name,
              success: true,
              message: response.data["success"] || "Created"
            });
            successCount++;
          } else {
            this.uploadResults.push({
              row: row._rowNumber,
              name: payload.name,
              success: false,
              message:
                response.data["error"] ||
                `Unexpected status ${response.status}`
            });
            failCount++;
          }
        } catch (err) {
          this.uploadResults.push({
            row: row._rowNumber,
            name: payload.name,
            success: false,
            message:
              err?.response?.data?.error || err.message || "Request failed"
          });
          failCount++;
        }
      }

      this.uploading = false;

      if (successCount > 0) {
        this.$emit(
          "success",
          `Bulk upload complete: ${successCount} record(s) created` +
            (failCount > 0 ? `, ${failCount} failed` : ".")
        );
      }
      if (failCount > 0 && successCount === 0) {
        this.$emit(
          "error",
          `Bulk upload failed: all ${failCount} record(s) encountered errors.`
        );
      }
    },

    _resolveZoneId(zoneName) {
      const zones = this.$store.getters.zone;
      const match = zones.find(
        (z) =>
          (z.zone_name || z.name || "").toLowerCase() ===
          zoneName.toLowerCase()
      );
      return match ? match.zone_id || match.id : null;
    }
  }
};
</script>
